/**
 * Brand Theme — single source of truth for all component styling.
 * Edit this file to rebrand all video components at once.
 */

export const THEME = {
  // ── Colors (matches Antelope leadgen apps tailwind config) ──
  colors: {
    bg: '#0A1629',           // Primary dark background (antelope-bg)
    bgCard: '#1A2C2E',       // Card/module background (antelope-card)
    bgSecondary: '#111E36',  // Header/footer/inputs/nested (antelope-secondary)
    bgCardHover: '#162240',  // Card hover state
    accent: '#34A49F',       // Primary accent teal (antelope-accent)
    accentHover: '#2aa19b',  // Hover state for teal (antelope-accent-hover)
    accentLight: '#5CC4BF',  // Light accent for secondary use
    orange: '#F97316',       // Brand "A" logo mark (antelope-orange)
    text: '#FFFFFF',         // Primary text (text-white)
    textMuted: '#94A3B8',    // Secondary text (≈ text-gray-400)
    textDim: '#64748B',      // Tertiary text (≈ text-gray-500)
    border: 'rgba(55,65,81,0.5)', // Semi-transparent (gray-700/50)
    success: '#22C55E',      // Positive metrics (green-400)
    warning: '#FBBF24',      // Caution/highlight
    error: '#EF4444',        // Negative metrics (red-400)
    purple: '#8B5CF6',       // Accent category (purple-500)
    blue: '#3B82F6',         // Accent category (blue-500)
    chart: ['#34A49F', '#5CC4BF', '#FBBF24', '#EF4444', '#8B5CF6', '#F97316'],
  },

  // ── Typography ──
  font: {
    family: "'Poppins', sans-serif",
    // Responsive sizes — multiply by scale factor from component
    title: 36,
    subtitle: 22,
    body: 16,
    label: 13,
    small: 11,
    weight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      black: 900,
    },
  },

  // ── Spacing (base unit = 8px) ──
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
  },

  // ── Borders ──
  radius: {
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    pill: 9999,
  },
} as const;

/**
 * Scale factor for responsive components.
 * Components receive a height prop — use this to scale typography and spacing.
 *
 * Usage:
 *   const s = getScale(height);
 *   fontSize: THEME.font.title * s
 *
 * At 845px (SPLIT_SCREEN): scale = 1.0
 * At 1920px (FULL_BROLL): scale = ~1.5 (bigger text, more room)
 * At 400px (thumbnail):   scale = ~0.6 (tighter)
 */
const BASE_HEIGHT = 845;

export const getScale = (height: number): number => {
  const raw = height / BASE_HEIGHT;
  // Clamp between 0.5 and 2.0 to prevent extremes
  return Math.max(0.5, Math.min(2.0, raw));
};

/**
 * Layout mode — components can adapt content density.
 * FULL_BROLL (1920px) = 'full' → show more detail, extra rows, bigger charts
 * SPLIT_SCREEN (845px) = 'compact' → focused summary
 */
export const getLayoutMode = (height: number): 'full' | 'compact' => {
  return height > 1000 ? 'full' : 'compact';
};
